import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { useLazyListVendorPricingsQuery } from "store/services/vendorProfileApi";

const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  paymentStatuses: [],
};

const PAYMENT_STATUSES = ["Paid", "Not Paid", "Refunded"];

const ExportVendorPriceList = ({ vendorId }) => {
  const [showDialog, setShowDialog] = useState(false);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [fetchPricings] = useLazyListVendorPricingsQuery();

  const handleDownload = async (data) => {
    const { dateRange, paymentStatuses } = data;

    const fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
    const toDate = moment(dateRange[1]).format("YYYY-MM-DD");

    const payload = {
      vendorId,
      ...(paymentStatuses.length > 0 && { paymentStatus: paymentStatuses }),
      fromDate,
      toDate,
      limit: 10000, // Increase limit for exports
    };

    try {
      const responseData = await fetchPricings(payload).unwrap();

      if (!responseData?.data || responseData.data.length === 0) {
        toast.info(
          "No pricing records found for the selected date range and filters."
        );
        return;
      }

      const formattedData = responseData.data.map((row, index) => ({
        SN: index + 1,
        "Shipment ID": row?.shipment?.shipmentId || "N/A",
        "Fee Item": row?.feeItem?.name || "N/A",
        "Weight (kg)": row?.shipment?.weight?.toFixed(2) || "0.00",
        "Amount (Rs.)": row?.charge?.toFixed(2) || "0.00",
        "Payment Type": row?.chargeType || "N/A",
        Payer: row?.payer || "N/A",
        "Payment Location": row?.paymentLocation || "N/A",
        "Payment Status":
          row?.paymentStatus ||
          row?.discountStatus ||
          row?.remitStatus ||
          "N/A",
        "Created At": row.createdAt
          ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
        "Shipment Status": row?.shipment?.status || "N/A",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Vendor_Pricing_List_${moment().format("YYYYMMDD")}`,
        sheetName: "Vendor Pricing",
        headerStyle: {
          fill: { fgColor: { rgb: "4472C4" } }, // Blue header
          font: { color: { rgb: "FFFFFF" }, bold: true },
        },
      });

      if (responseData.pagination?.total > responseData.data.length) {
        toast.info(
          `Downloaded ${responseData.data.length} of ${responseData.pagination.total} records. ` +
            `Some records were not included due to the maximum limit.`
        );
      } else {
        toast.success("Pricing data downloaded successfully");
      }
    } catch (error) {
      console.error("Error fetching pricing data:", error);
      toast.error(
        "Error fetching pricing data: " + (error.message || "Unknown error")
      );
    }
    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Vendor Pricing" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Vendor Pricing Excel"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        sx={{ "& .MuiDialog-paper": { maxWidth: "600px", width: "100%" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
          <Controller
            name="paymentStatuses"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={PAYMENT_STATUSES}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Payment Status"
                    size="small"
                    sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      sx={{
                        m: 0.5,
                        height: 24,
                        "& .MuiChip-label": { fontSize: "0.75rem" },
                      }}
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ fontSize: "0.875rem" }}>{option}</span>
                  </li>
                )}
                sx={{ width: "100%" }}
                ListboxProps={{ style: { maxHeight: 200 } }}
              />
            )}
          />

          <Controller
            name="dateRange"
            control={control}
            rules={{ required: "Date range is required" }}
            render={({ field, fieldState: { error } }) => (
              <DateRangeToggle
                {...field}
                label="Select Date Range"
                size="small"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Box>
      </AppConfirmDialog>
    </>
  );
};

export default ExportVendorPriceList;
